import React, { useState, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";

import { AppDispatch, RootState } from "../../../../redux/store";
import { fetchDGCoinProviders } from "../../../../features/auth/store/dgcoinProviderSlice";
import DGCoinProvidersTable from "./DGCoinProvidersTable";
import { CreateDGCoinProviderModal } from "../../Modal/DGCoins/CreateDGCoinProviderModal";
import {
  ExternalContainer,
  InternalContainer,
} from "../../../../components/Reusable/Layout";
import { Button } from "../../../../components/Reusable/Button";
import { EditDGCoinProviderModal } from "../../Modal/DGCoins/EditDGCoinProviderModal";
import { DeleteDGCoinProviderModal } from "../../Modal/DGCoins/DeleteDGCoinProviderModal";
import ProviderFaqManagementModal from "../../Modal/ProviderFaqManagementModal";

const DGCoinProviders: React.FC = () => {
  const dispatch = useDispatch<AppDispatch>();
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [isFaqModalOpen, setIsFaqModalOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [selectedProvider, setSelectedProvider] = useState<number | null>(null);
  const [selectedFaqProvider, setSelectedFaqProvider] = useState<{
    id: number;
    name: string;
  } | null>(null);

  const { dgcoinProviders, loading, error } = useSelector(
    (state: RootState) => state.dgcoinProviders,
  );

  useEffect(() => {
    dispatch(fetchDGCoinProviders({}));
  }, [dispatch]);

  const openDeleteModal = async (id: number) => {
    setSelectedProvider(id);
    setIsDeleteModalOpen(true);
  };

  const openEditModal = async (id: number) => {
    setSelectedProvider(id);
    setIsEditModalOpen(true);
  };
  const openFaqModal = (provider: { id: number; name: string }) => {
    setSelectedFaqProvider(provider);
    setIsFaqModalOpen(true);
  };

  const filteredProviders = dgcoinProviders.filter((provider) => {
    const matchesStatus = !statusFilter || provider.status === statusFilter;
    const matchesSearch =
      !searchTerm ||
      provider.name.toLowerCase().includes(searchTerm.toLowerCase());
    return matchesStatus && matchesSearch;
  });

  return (
    <ExternalContainer>
      <div className="h-screen dark:bg-[#FFFFFF1A] w-[1030px]">
        <InternalContainer headerText="DG Coin Providers">
          <div className="flex justify-between items-center mb-4">
            <div className="flex gap-4 mb-4">
              <input
                type="text"
                placeholder="Search by name"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="border p-2 rounded"
              />
              <select
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
                className="border p-2 rounded"
              >
                <option value="">All Status</option>
                <option value="on">Active</option>
                <option value="off">Inactive</option>
              </select>
            </div>
            <Button
              onClick={() => setIsCreateModalOpen(true)}
              bgColor="bg-gradient-to-r from-[#027EC2] to-[#039EF2]"
              textColor="text-white"
              borderRadius="rounded-lg"
              className="w-[200px] h-10 ml-6"
            >
              New Provider
            </Button>
          </div>

          <DGCoinProvidersTable
            providers={filteredProviders}
            loading={loading}
            onDelete={openDeleteModal}
            onEdit={openEditModal}
            onManageFaq={openFaqModal}
          />

          <CreateDGCoinProviderModal
            isOpen={isCreateModalOpen}
            onClose={() => setIsCreateModalOpen(false)}
          />

          <EditDGCoinProviderModal
            isOpen={selectedProvider !== null && isEditModalOpen}
            id={selectedProvider}
            onClose={() => {
              setIsEditModalOpen(false);
              setSelectedProvider(null);
            }}
          />

          <DeleteDGCoinProviderModal
            id={selectedProvider}
            isOpen={isDeleteModalOpen}
            onClose={() => {
              setIsDeleteModalOpen(false);
              setSelectedProvider(null);
            }}
          />
          <ProviderFaqManagementModal
            isOpen={isFaqModalOpen}
            onClose={() => {
              setIsFaqModalOpen(false);
              setSelectedFaqProvider(null);
            }}
            providerId={selectedFaqProvider?.id ?? null}
            providerName={selectedFaqProvider?.name}
            providerEndpoint="dg-coins-providers"
          />
        </InternalContainer>
        {error && <p className="text-red-500 mt-2">{error}</p>}
      </div>
    </ExternalContainer>
  );
};

export default DGCoinProviders;
